import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './StatsPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Stats {
  total_translations: number;
  total_characters: number;
  today: number;
  week: number;
  month: number;
  top_language_pairs: Array<{
    source: string;
    target: string;
    count: number;
  }>;
}

const LANGUAGE_NAMES: { [key: string]: string } = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  pt: 'Português',
  ru: 'Русский',
  ja: '日本語',
  zh: '中文'
};

function StatsPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API_URL}/api/stats`);
      setStats(response.data);
    } catch (err: any) {
      setError('Fehler beim Laden der Statistiken');
      console.error('Stats error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="stats-page">
        <div className="stats-loading">
          <div className="loading-spinner"></div>
          <p>Lade Statistiken...</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="stats-page">
        <div className="stats-error">
          <p>{error || 'Keine Daten verfügbar'}</p>
          <button onClick={loadStats} className="retry-button">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stats-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <h1 className="logo">Statistiken</h1>
            <a href="/" className="back-link">← Zurück zum Übersetzer</a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          <div className="stats-grid">
            {/* Overview Cards */}
            <div className="stat-card large">
              <div className="stat-label">Gesamt Übersetzungen</div>
              <div className="stat-value">{stats.total_translations.toLocaleString('de-DE')}</div>
            </div>

            <div className="stat-card large">
              <div className="stat-label">Gesamt Zeichen</div>
              <div className="stat-value">{stats.total_characters.toLocaleString('de-DE')}</div>
            </div>

            {/* Time-based Stats */}
            <div className="stat-card">
              <div className="stat-label">Heute</div>
              <div className="stat-value">{stats.today.toLocaleString('de-DE')}</div>
              <div className="stat-sublabel">Übersetzungen</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Diese Woche</div>
              <div className="stat-value">{stats.week.toLocaleString('de-DE')}</div>
              <div className="stat-sublabel">Übersetzungen</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Dieser Monat</div>
              <div className="stat-value">{stats.month.toLocaleString('de-DE')}</div>
              <div className="stat-sublabel">Übersetzungen</div>
            </div>

            {/* Top Language Pairs */}
            <div className="stat-card wide">
              <div className="stat-label">Meistgenutzte Sprachpaare</div>
              <div className="language-pairs-list">
                {stats.top_language_pairs.length > 0 ? (
                  stats.top_language_pairs.map((pair, index) => (
                    <div key={index} className="language-pair-item">
                      <div className="language-pair-info">
                        <span className="pair-rank">{index + 1}.</span>
                        <span className="pair-languages">
                          {LANGUAGE_NAMES[pair.source] || pair.source} → {LANGUAGE_NAMES[pair.target] || pair.target}
                        </span>
                      </div>
                      <div className="pair-count">{pair.count.toLocaleString('de-DE')}</div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">Noch keine Daten vorhanden</p>
                )}
              </div>
            </div>
          </div>

          {/* Refresh Button */}
          <div className="refresh-section">
            <button onClick={loadStats} className="refresh-button">
              ↻ Aktualisieren
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="text-muted">
            Powered by Azure OpenAI • Intranet Translation Service
          </p>
        </div>
      </footer>
    </div>
  );
}

export default StatsPage;
